import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Building2, LogOut, ShieldCheck, Save, ArrowLeft, CheckCircle2 } from "lucide-react";
import { StatusPill } from "@/components/instapharm/StatusPill";

export default function InstapharmPro() {
  const navigate = useNavigate();
  const [account, setAccount] = useState(null);
  const [establishment, setEstablishment] = useState(null);
  const [config, setConfig] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);

  const [status, setStatus] = useState("");
  const [waitMinutes, setWaitMinutes] = useState("");
  const [note, setNote] = useState("");

  const loadAccount = async () => {
    const { data } = await api.get("/instapharm/account");
    setEstablishment(data);
    setStatus(data.status || "");
    setWaitMinutes(data.waitMinutes != null ? String(data.waitMinutes) : "");
    setNote(data.statusNote || "");
  };

  useEffect(() => {
    api.get("/instapharm/config").then((r) => setConfig(r.data)).catch(() => {});
    api.get("/instapharm/auth/me")
      .then(async (r) => { setAccount(r.data); await loadAccount(); })
      .catch(() => {})
      .finally(() => setChecking(false));
  }, []);

  const login = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/instapharm/auth/login", { email, password });
      setAccount(data);
      await loadAccount();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const logout = async () => {
    await api.post("/instapharm/auth/logout").catch(() => {});
    setAccount(null); setEstablishment(null);
  };

  const save = async () => {
    if (!status) { toast.error("Choisissez un statut."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/instapharm/account/status", {
        status,
        waitMinutes: waitMinutes === "" ? null : parseInt(waitMinutes, 10),
        note: note || null,
      });
      setEstablishment(data);
      toast.success("Statut mis à jour et daté.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const confirmInfo = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/instapharm/account/confirm");
      setEstablishment(data);
      toast.success("Merci ! Vos informations sont confirmées à jour.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const Wrap = ({ children }) => (
    <div className="min-h-screen bg-[#F9F8F6]">
      <div className="max-w-md mx-auto min-h-screen bg-[#F9F8F6] shadow-[0_0_40px_rgba(0,0,0,0.05)] px-5 py-8">{children}</div>
    </div>
  );

  if (checking) return <Wrap><p className="text-slate-500">Chargement…</p></Wrap>;

  if (!account) {
    return (
      <Wrap>
        <button onClick={() => navigate("/instapharm")} data-testid="pro-back" className="inline-flex items-center gap-1 text-sm text-slate-500 mb-6"><ArrowLeft className="h-4 w-4" /> Retour</button>
        <div className="h-11 w-11 rounded-xl bg-[#1E3A8A] grid place-items-center text-white mb-4"><Building2 className="h-5 w-5" /></div>
        <h1 className="font-['Outfit'] text-2xl font-bold text-slate-900 mb-1">Espace établissement</h1>
        <p className="text-sm text-slate-500 mb-6">Mettez à jour le statut de votre établissement en temps réel.</p>
        <div className="space-y-3">
          <div>
            <Label className="text-sm">Email</Label>
            <Input data-testid="pro-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="h-12 rounded-xl mt-1.5 bg-white" />
          </div>
          <div>
            <Label className="text-sm">Mot de passe</Label>
            <Input data-testid="pro-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} onKeyDown={(e) => e.key === "Enter" && login()} className="h-12 rounded-xl mt-1.5 bg-white" />
          </div>
          <Button data-testid="pro-login-btn" onClick={login} disabled={loading || !email || !password} className="w-full h-14 rounded-xl bg-[#1E3A8A] hover:bg-[#1E40AF]">Se connecter</Button>
        </div>
      </Wrap>
    );
  }

  return (
    <Wrap>
      <button data-testid="pro-back-in" onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800 mb-4"><ArrowLeft className="h-4 w-4" /> Retour</button>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <div className="h-10 w-10 rounded-xl bg-[#1E3A8A] grid place-items-center text-white"><Building2 className="h-5 w-5" /></div>
          <div>
            <div className="font-['Outfit'] font-bold text-slate-900 leading-none">Espace établissement</div>
            <div className="text-xs text-slate-500 mt-0.5">{account.email}</div>
          </div>
        </div>
        <button data-testid="pro-logout" onClick={logout} className="text-slate-400 hover:text-slate-700"><LogOut className="h-5 w-5" /></button>
      </div>

      {establishment && (
        <>
          <div className="bg-white rounded-xl border border-slate-200 p-4 mb-5">
            <div className="text-[11px] tracking-[0.1em] uppercase font-bold text-[#1E3A8A]">{establishment.typeLabel}</div>
            <h2 className="font-['Outfit'] font-semibold text-slate-900 mt-0.5">{establishment.name}</h2>
            <div className="mt-2 flex items-center gap-2">
              <StatusPill status={establishment.status} testid="pro-current-status" />
              {establishment.lastVerifiedAt && <span className="text-xs text-slate-400 flex items-center gap-1"><ShieldCheck className="h-3.5 w-3.5" /> {new Date(establishment.lastVerifiedAt).toLocaleString("fr-FR")}</span>}
            </div>
          </div>

          {/* Confirmation périodique des informations */}
          <div className="bg-white rounded-xl border border-slate-200 p-4 mb-5">
            <div className="flex items-start gap-3">
              <div className="h-9 w-9 rounded-lg bg-emerald-50 grid place-items-center text-emerald-600 shrink-0"><CheckCircle2 className="h-5 w-5" /></div>
              <div className="flex-1">
                <div className="text-sm font-semibold text-slate-800">Vos informations sont-elles toujours exactes ?</div>
                <p className="text-xs text-slate-500 mt-0.5">Confirmez d'un clic (adresse, téléphone, horaires). La date de dernière vérification est affichée aux patients.</p>
                {establishment.lastConfirmedAt && (
                  <p data-testid="pro-last-confirmed" className="text-xs text-emerald-700 mt-1.5 flex items-center gap-1"><ShieldCheck className="h-3.5 w-3.5" /> Confirmées le {new Date(establishment.lastConfirmedAt).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}</p>
                )}
              </div>
            </div>
            <Button data-testid="pro-confirm-info" onClick={confirmInfo} disabled={loading} variant="outline" className="w-full h-12 rounded-xl mt-3 border-emerald-300 text-emerald-700 hover:bg-emerald-50 gap-2">
              <CheckCircle2 className="h-4 w-4" /> Confirmer que mes infos sont exactes
            </Button>
          </div>

          <div className="text-xs tracking-[0.1em] uppercase font-bold text-slate-500 mb-3">Mettre à jour le statut</div>
          <div className="space-y-4">
            <div>
              <Label className="text-sm">Statut actuel</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger data-testid="pro-status-select" className="h-12 rounded-xl mt-1.5 bg-white"><SelectValue placeholder="Choisir un statut" /></SelectTrigger>
                <SelectContent>
                  {config?.statusOptions.map((s) => <SelectItem key={s.value} value={s.value} data-testid={`pro-status-opt-${s.value}`}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm">Délai d'attente estimé (minutes) — optionnel</Label>
              <Input data-testid="pro-wait" type="number" min="0" value={waitMinutes} onChange={(e) => setWaitMinutes(e.target.value)} placeholder="Laisser vide si inconnu" className="h-12 rounded-xl mt-1.5 bg-white" />
            </div>
            <div>
              <Label className="text-sm">Note (optionnel)</Label>
              <Textarea data-testid="pro-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Ex : appeler avant de venir" className="rounded-xl mt-1.5 bg-white" />
            </div>
            <Button data-testid="pro-save-btn" onClick={save} disabled={loading} className="w-full h-14 rounded-xl bg-[#1E3A8A] hover:bg-[#1E40AF] gap-2"><Save className="h-5 w-5" /> Enregistrer et dater</Button>
            <p className="text-[11px] text-slate-400">Le statut est enregistré et daté automatiquement, puis pris en compte immédiatement dans les résultats des patients.</p>
          </div>
        </>
      )}
    </Wrap>
  );
}
